import "../CSS/VcMapAirFreshner.css";
import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { Modal, InputNumber, Button, message } from "antd";
import { FaQrcode, FaTimes, FaLink, FaUnlink, FaSyncAlt } from "react-icons/fa";
import { MdOutlineAir } from "react-icons/md";
import { TbDeviceAnalytics } from "react-icons/tb";
import { getAPIHostURL } from "../../ClientConfig";
import { AppRelevantDataContext } from "../../AppContext";
import { QrScanner } from "@yudiel/react-qr-scanner";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import {DEVICE_TYPE_HHM, DEVICE_TYPE_SAU} from "../../VcConstants";
import { useTable, useBlockLayout, useResizeColumns,
         usePagination, useSortBy, useFilters } from 'react-table';
import { Pagination, Select } from 'antd';
import { FaArrowDownLong, FaArrowUpLong } from 'react-icons/fa6';
import { FaSearch } from 'react-icons/fa';
import '../CSS/ReactTable.css';

// ─── helpers ────────────────────────────────────────────────────────────────
const isValidDeviceID = (id) => /^[0-9A-F]{12}$/.test(id);
const EMPTY_DEVICE = { id: "", verified: false, deviceState: "", error: "", scanning: false, verifying: false };

const formatDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d) ? "—" : d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

// ── reusable DeviceCard ────────────────────────────────────────────────────
const DeviceCard = ({ label, icon: Icon, accentClass, device, setter, deviceKind, placeholder, inputRef, onChangeID,
          toggleScan,
          verifyDevice,
          onScan }) => (
  <div className={`map-device-card ${accentClass}`}>
    <div className="map-card-header">
      <span className="map-card-icon-wrap"><Icon /></span>
      <span className="map-card-label">{label}</span>
      {device.verified && (
        <span className="map-verified-badge"><CheckCircleFilled /> Verified</span>
      )}
    </div>

    <div className="map-input-row">
      <div className="map-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className={`map-input ${device.error ? "map-input-error" : device.verified ? "map-input-ok" : ""}`}
          value={device.id}
          onChange={onChangeID(setter)}
          placeholder={placeholder}
          maxLength={12}
          autoComplete="off"
          spellCheck={false}
        />
        <FaQrcode
          className="map-qr-icon"
          title="Scan QR"
          onClick={() => toggleScan(setter)}
        />
      </div>
      <button
        className="map-verify-btn"
        type="button"
        onClick={() => verifyDevice(device, setter, deviceKind)}
        disabled={!device.id || device.verifying}
      >
        {device.verifying ? "…" : "Verify"}
      </button>
      {device.id && (
        <button
          className="map-clear-btn"
          type="button"
          title="Clear"
          onClick={() => setter({ ...EMPTY_DEVICE })}
        >
          <FaTimes />
        </button>
      )}
    </div>

    {device.scanning && (
      <div className="map-qr-scanner-wrap">
        <QrScanner
          onDecode={onScan(setter)}
          onError={(e) => console.error(e)}
          constraints={{ facingMode: "environment" }}
        />
      </div>
    )}

    {device.error && (
      <div className="map-field-error"> {device.error}</div>
    )}

    {/* {device.verified && (
      <div className="map-device-state-row">
        <span className="map-state-label">State:</span>
        <span className="map-state-value">{device.deviceState}</span>
      </div>
    )} */}
  </div>
);

// Case-insensitive filter — same helper used in VcProduct
const filterCaseInsensitive = (rows, id, filterValue) =>
  rows.filter((row) => {
    console.log("filterCaseInsensitive called with filterValue:", row, id, filterValue);
    const rowValue = row.values[id];
    console.log("Row value:", rowValue);
    console.log("----", String(rowValue).toLowerCase().includes(filterValue.toLowerCase()));
    return rowValue !== undefined
      ? String(rowValue).toLowerCase().includes(filterValue.toLowerCase())
      : false;
  });

// Per-column search input rendered under each header
function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <div style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", paddingLeft: "0.1rem", paddingRight: "0.3rem" }}>
      <FaSearch style={{ marginRight: "0.3rem", color: "var(--secondaryColor)", fontSize: "1rem" }} />
      <input
        value={filterValue || ""}
        onChange={(e) => setFilter(e.target.value || undefined)}
        placeholder="Search"
        style={{ fontSize: "0.8rem", width: "85%", height: "100%", padding: "0.3rem", border: "1px solid rgba(0,0,0,.1)" }}
      />
    </div>
  );
}

const defaultPropGetter = () => ({});

// MappingReactTable — same structure/hooks as VcProduct's ReactTable
const MappingReactTable = ({ columns, data, passedStateVariable, getCellProps = defaultPropGetter }) => {

  const filterTypes = React.useMemo(() => ({
    text: (rows, id, filterValue) =>
      rows.filter((row) => {
        const rowValue = row.values[id];
        return rowValue !== undefined
          ? String(rowValue).toLowerCase().includes(String(filterValue).toLowerCase())
          : true;
      }),
  }), []);

  const defaultColumn = React.useMemo(() => ({
    minWidth: 30,
    width: 150,
    Filter: DefaultColumnFilter,
    canFilter: true,
  }), []);

  const {
    getTableProps, getTableBodyProps, headerGroups,
    page, canPreviousPage, canNextPage,
    pageOptions, gotoPage, setPageSize, prepareRow,
    state: { pageIndex, pageSize },
  } = useTable(
    { columns, data, defaultColumn, initialState: { pageSize: 10 }, filterTypes },
    useBlockLayout,
    useResizeColumns,
    useFilters,
    useSortBy,
    usePagination,
  );

  // Jump to page 1 whenever data refreshes
  React.useEffect(() => {
    if (passedStateVariable === true) gotoPage(0);
  }, [passedStateVariable]);

  const createEmptyRow = (noData = false) => (
    <tr className="tr" style={{ textAlign: "left", paddingLeft: "1rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" }}>
      <td className="td">
        {noData
          ? <div><span>&nbsp;No Mapping Records Found.</span></div>
          : <div><span>&nbsp;</span></div>}
      </td>
    </tr>
  );

  const getEmptyRow = () => {
    let emptyRows = [];
    if (page.length % pageSize !== 0 && !canNextPage) {
      for (let i = 0; i < pageSize - (page.length % pageSize); i++)
        emptyRows.push(createEmptyRow(false));
    }
    if (data.length === 0 || page.length === 0) {
      for (let i = 0; i < pageSize; i++)
        emptyRows.push(i === 0 ? createEmptyRow(true) : createEmptyRow(false));
    }
    return emptyRows;
  };

  return (
    <div>
      <div className="tableWrap">
        <table {...getTableProps()} style={{ overflow: "auto" }}>

          {/* Column headers with sort arrows */}
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} className="trForHeader">
                {hg.headers.map((col) => (
                  <th {...col.getHeaderProps(col.getSortByToggleProps())} className="tdForHeader">
                    <div className="Header">
                      {col.render("Header")}
                      <div className="fa">
                        {col.isSorted ? col.isSortedDesc ? <FaArrowDownLong /> : <FaArrowUpLong /> : ""}
                      </div>
                    </div>
                    <div
                      {...col.getResizerProps()}
                      className={`resizer ${col.isResizing ? "isResizing" : ""}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Per-column search filters */}
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} className="trforSearchField">
                {hg.headers.map((col) => (
                  <th {...col.getHeaderProps()} className="tdForSearchField">
                    <div>{col.canFilter ? col.render("Filter") : null}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="tr" style={{ color: "black", alignItems: "center" }}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps([{ style: cell.column.style }, getCellProps(cell)])} className="td">
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
            {getEmptyRow()}
          </tbody>
        </table>
      </div>

      {/* Ant Design pagination — matches VcProduct */}
      <div className="w-100 d-flex justify-content-end antdPagination">
        <div className="d-flex py-1 justify-content-end align-items-center">
          <Pagination
            showQuickJumper
            defaultCurrent={pageIndex + 1}
            total={pageOptions.length * 10}
            onChange={(pg) => gotoPage(pg - 1)}
            showSizeChanger={false}
            current={pageIndex + 1}
          />
          <Select
            style={{ marginRight: "1rem", marginLeft: "1.5rem" }}
            aria-label="rows per page"
            className="paginationDropdown mySelector"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e))}
            options={[
              { label: "5 rows",   value: 5   },
              { label: "10 rows",  value: 10  },
              { label: "20 rows",  value: 20  },
              { label: "50 rows",  value: 50  },
              { label: "100 rows", value: 100 },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

function VcMapAirFreshner(props) {
  const context = useContext(AppRelevantDataContext);

  const [airFreshner,    setAirFreshner]   = useState({ ...EMPTY_DEVICE });
  const [gandhvedh,      setGandhvedh]     = useState({ ...EMPTY_DEVICE });
  const [mappingStatus,  setMappingStatus] = useState(null);
  const [actionLoading,  setActionLoading] = useState(false);
  const [sprayCountRow, setSprayCountRow] = useState(null);
  const [sprayCount, setSprayCount] = useState(null);
  const [sprayCountLoading, setSprayCountLoading] = useState(false);
  const [sprayCountError, setSprayCountError] = useState("");

  // ── Mapped devices report ─────────────────────────────────────────────────
  const [mappedList,     setMappedList]    = useState([]);
  const [reportLoading,  setReportLoading] = useState(false);
  const [reportError,    setReportError]   = useState("");
  const [demapRowStatus, setDemapRowStatus]= useState({}); // { rowKey: { loading, error } }
  const [searchTerm,     setSearchTerm]    = useState("");

  // ── input refs (fix: controlled input single-char bug via ref) ─────────────
  const afInputRef = useRef(null);
  const gvInputRef = useRef(null);

  const mappingTableColumns = React.useMemo(() => [
    {
      Header: () => <div className="AlertLogTableHeader">#</div>,
      accessor: "rowIndex",
      width: 10,
      disableFilters: true,
      disableSortBy: true,
      style: { textAlign: "center", paddingLeft: "0.5rem", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ row }) => <span>{row.index + 1}</span>,
    },
    {
      Header: () => <div className="AlertLogTableHeader">SAU<br />Device ID</div>,
      accessor: "SAUDeviceID",
      width: 140,
      filter: filterCaseInsensitive,
      style: { textAlign: "left", paddingLeft: "1rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ value }) => <span className="map-device-chip">{value}</span>,
    },
    {
      Header: () => <div className="AlertLogTableHeader">HHM<br />Device ID</div>,
      accessor: "HHMDeviceID",
      width: 140,
      filter: filterCaseInsensitive,
      style: { textAlign: "left", paddingLeft: "1rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ value }) => <span className="map-device-chip">{value}</span>,
    },
    {
      Header: () => <div className="AlertLogTableHeader">Status</div>,
      accessor: "Status",
      width: 160,
      filter: filterCaseInsensitive,
      style: { textAlign: "center", paddingLeft: "1rem", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ value }) => {
        const isActive = value === "Mapped" || value === "MapReceived" || value === "MapRequested";
        return (
          <span className={`map-status-pill ${isActive ? "pill-active" : "pill-inactive"}`}>
            {value}
          </span>
        );
      },
    },
    {
      Header: () => <div className="AlertLogTableHeader">Mapped On</div>,
      accessor: "MappedOnDtTm",
      width: 185,
      filter: filterCaseInsensitive,
      style: { textAlign: "left", paddingLeft: "1rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ value }) => <span>{formatDate(value)}</span>,
    },
    {
      Header: () => <div className="AlertLogTableHeader">Mapped<br />Requested By</div>,
      accessor: "MapRequestedByUser",
      width: 175,
      filter: filterCaseInsensitive,
      style: { textAlign: "left", paddingLeft: "1rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: () => <div className="AlertLogTableHeader">De-mapped On</div>,
      accessor: "DemappedOnDtTm",
      width: 185,
      filter: filterCaseInsensitive,
      style: { textAlign: "left", paddingLeft: "1rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ value }) => <span>{formatDate(value)}</span>,
    },
    {
      Header: () => <div className="AlertLogTableHeader">De-mapped<br />Requested By</div>,
      accessor: "DemapRequestedByUser",
      width: 175,
      filter: filterCaseInsensitive,
      style: { textAlign: "left", paddingLeft: "1rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: () => <div className="AlertLogTableHeader">Action</div>,
      accessor: "action",
      width: 250,
      disableFilters: true,
      disableSortBy: true,
      style: { textAlign: "center", paddingLeft: "0.5rem", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ row }) => {
        const rowData   = row.original;
        const isActive  = rowData.Status === "Mapped" || rowData.Status === "MapReceived" || rowData.Status === "MapRequested";
        const key       = `${rowData.SAUDeviceID}_${rowData.HHMDeviceID}`;
        const rowStatus = demapRowStatus[key] || {};
        return isActive ? (
          <div className="map-row-action-wrap">
            <div className="map-row-action-buttons">
              <button
                className="map-row-demap-btn"
                onClick={() => confirmDemapping(rowData)}
                disabled={rowStatus.loading}
                title="De-map this pair"
              >
                <FaUnlink />
                {rowStatus.loading ? "Removing…" : "De-map"}
              </button>
              {rowData.Status === "Mapped" && (
                <button
                  type="button"
                  className="map-row-spray-count-btn"
                  onClick={() => {
                    setSprayCount(rowData.AdditionalInfo?.SetMaxSauInfo?.Count ?? null);
                    setSprayCountError("");
                    setSprayCountRow(rowData);
                  }}
                  disabled={rowStatus.loading}
                  title="Spray Count"
                >
                  Spray Count
                </button>
              )}
            </div>
            {rowStatus.error && <span className="map-row-error">{rowStatus.error}</span>}
          </div>
        ) : (
          <span className="map-td-na">—</span>
        );
      },
    },
    {
      Header: () => <div className="AlertLogTableHeader">Last Modified Time</div>,
      accessor: "LastModifiedTime",
      width: 185,
      filter: filterCaseInsensitive,
      style: { textAlign: "left", paddingLeft: "1rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", height: "2.6rem" },
      Cell: ({ value }) => <span>{formatDate(value)}</span>,
    },
  ], [demapRowStatus]);

  // load report on mount and after any map/demap action
  useEffect(() => {
    fetchMappedDevices();
  }, []);

  const fetchMappedDevices = async () => {
    setReportLoading(true);
    setReportError("");
    try {
      const response = await axios.post(`${getAPIHostURL()}/wclient/getSAUHHMDviceMappingInfo`, {
        LoggedInUserID: context.loggedInUserInfo.userID,
      });
      if (response.data.code === "SUCCESS") {
        setMappedList(response.data.SAUHHMDeviceMappingInfo || []);
      } else {
        setReportError(response.data.message || "Failed to load mappings.");
      }
    } catch {
      setReportError("Network error. Could not load mappings.");
    } finally {
      setReportLoading(false);
    }
  };

  // ── input change — fixed: use functional setter to avoid stale closure ─────
  const onChangeID = (setter) => (e) => {
        e.preventDefault();

    const value = e.target.value.toUpperCase().replace(/[^0-9A-F]/g, "");
    setter((prev) => ({ ...prev, id: value, verified: false, deviceState: "", error: "" }));
  };

  // ── QR toggle ─────────────────────────────────────────────────────────────
  const toggleScan = async (setter) => {
    try {
      const perm = await navigator.permissions.query({ name: "camera" });
      if (perm.state === "denied") {
        setter((prev) => ({ ...prev, error: "Camera permission denied." }));
        return;
      }
    } catch { /* permissions API not available on all browsers */ }
    setter((prev) => ({ ...EMPTY_DEVICE, scanning: !prev.scanning }));
  };

  const onScan = (setter) => (data) => {
    if (!data) return;
    setter({ ...EMPTY_DEVICE, id: data.toUpperCase() });
  };

  // ── verify ────────────────────────────────────────────────────────────────
  const verifyDevice = async (device, setter, deviceKind) => {
    const id = device.id.trim();
    if (!id) {
      setter((prev) => ({ ...prev, error: "Please enter a Device ID." }));
      return;
    }
    if (!isValidDeviceID(id)) {
      setter((prev) => ({ ...prev, error: "Invalid Device ID" }));
      return;
    }
    setter((prev) => ({ ...prev, error: "", verified: false, deviceState: "", verifying: true }));
    let jsonBody = {
      deviceID: id,
      deviceType: deviceKind
    }

    try {
      const response = await axios.post(`${getAPIHostURL()}/wclient/validateDevicesForSAUHHMMapping`, jsonBody).then(response => {

        if (response.data.code === "SUCCESS") {
          setter((prev) => ({ ...prev, verified: true, deviceState: response.data.DeviceState || "Active", error: "", verifying: false }));
        } else {
          setter((prev) => ({ ...prev, verified: false, error: response.data.failuremessage || "Verification failed.", verifying: false }));
        }
      })

      } catch {
        setter((prev) => ({ ...prev, error: "Network error. Please try again.", verifying: false }));
      }
  };

  const confirmMapping = () => {
    Modal.confirm({
      title: "Confirm Device Mapping",
      content: (
        <div>
          <p>Are you sure you want to map the following devices?</p>
          <p><strong>Air Freshener:</strong> {airFreshner.id}</p>
          <p><strong>Gandhvedh:</strong> {gandhvedh.id}</p>
        </div>
      ),
      okText: "Map",
      cancelText: "Cancel",
      okType: "primary",
      centered: true,
      onOk: () => handleMap(),
    });
  };

  const confirmDemapping = (row) => {
    Modal.confirm({
      title: "Confirm Device De-mapping",
      content: (
        <div>
          <p>Are you sure you want to de-map the following devices?</p>
          <p><strong>Air Freshener:</strong> {row.SAUDeviceID}</p>
          <p><strong>Gandhvedh:</strong> {row.HHMDeviceID}</p>
        </div>
      ),
      okText: "De-map",
      cancelText: "Cancel",
      okType: "danger",
      centered: true,
      onOk: () => handleRowDemap(row),
    });
  };

  // ── map ───────────────────────────────────────────────────────────────────
  const handleMap = async () => {
    if (!airFreshner.verified || !gandhvedh.verified) return;
    setActionLoading(true);
    setMappingStatus(null);
    try {
      const response = await axios.post(`${getAPIHostURL()}/wclient/mapSAUToHHM`, {
        SAUDeviceID: airFreshner.id,
        HHMDeviceID: gandhvedh.id,
        LoggedInUserID: context.loggedInUserInfo.userID,
      });

      if (response.data.code === "SUCCESS") {
        setMappingStatus({ type: "map", success: true, message: `Device Mapping requested for ${airFreshner.id} and ${gandhvedh.id}.` });
        setAirFreshner({ ...EMPTY_DEVICE });
        setGandhvedh({ ...EMPTY_DEVICE });
        fetchMappedDevices();
      } else {
        setMappingStatus({ type: "map", success: false, message: response.data.message || "Mapping failed." });
      }
    } catch {
      setMappingStatus({ type: "map", success: false, message: "Network error. Please try again." });
    } finally {
      setActionLoading(false);
    }
  };


  // ── demap from report row ─────────────────────────────────────────────────
  const handleRowDemap = async (row) => {
    const key = `${row.AirFreshnerDeviceID}_${row.GandhvedhDeviceID}`;
    setDemapRowStatus((prev) => ({ ...prev, [key]: { loading: true, error: "" } }));
    try {
      const response = await axios.post(`${getAPIHostURL()}/wclient/demapSAUToHHM`, {
        SAUDeviceID: row.SAUDeviceID,
        HHMDeviceID: row.HHMDeviceID,
        LoggedInUserID: context.loggedInUserInfo.userID,
      });
      if (response.data.code === "SUCCESS") {
        setDemapRowStatus((prev) => ({ ...prev, [key]: { loading: false, error: "" } }));
        fetchMappedDevices();
      } else {
        setDemapRowStatus((prev) => ({
          ...prev,
          [key]: {
            loading: false,
            error:   response.data.failuremessage || response.data.message || "De-map failed.",
          },
        }));
      }
    } catch {
      setDemapRowStatus((prev) => ({
        ...prev,
        [key]: { loading: false, error: "Network error." },
      }));
    }
  };

  const handleSprayCountUpdate = async () => {
    if (!sprayCountRow || sprayCountLoading || !Number.isSafeInteger(sprayCount) || sprayCount < 0) return;

    setSprayCountLoading(true);
    setSprayCountError("");
    try {
      const response = await axios.post(`${getAPIHostURL()}/wclient/setMaxSauForSAUDevice`, {
        DeviceID: sprayCountRow.SAUDeviceID,
        SetMaxSauInfo: { Count: sprayCount },
        LoggedInUserID: context.loggedInUserInfo.userID,
      });
      if (response.data.code === 200 && response.data.status === true) {
        await fetchMappedDevices();
        setSprayCountRow(null);
        message.success(response.data.message || "Spray count updated successfully.");
      } else {
        setSprayCountError(response.data.failuremessage || response.data.message || "Failed to update spray count.");
      }
    } catch {
      setSprayCountError("Network error. Please try again.");
    } finally {
      setSprayCountLoading(false);
    }
  };

  const bothVerified = airFreshner.verified && gandhvedh.verified;

  // ── filtered report rows ───────────────────────────────────────────────────
  const filteredList = mappedList.filter((row) => {
    const q = searchTerm.toLowerCase();
    return (
      !q ||
      row.SAUDeviceID?.toLowerCase().includes(q) ||
      row.HHMDeviceID?.toLowerCase().includes(q)
    );
  });



  return (
    <div className="map-page-root">
      <div className="map-container">

        {/* ── Heading ── */}
        <div className="map-heading-block">
          <h2 className="map-heading">Air Freshner ↔ Gandhvedh Mapping</h2>
          <p className="map-subheading">Verify both devices, then attach or detach them with a single click.</p>
        </div>

        {/* ── Info note ── */}
        {/* <div className="map-info-note">
          <strong>How it works:</strong> Enter or scan each Device ID and click <b>Verify</b>. Once both are verified, use <b>Map</b> to link them or <b>De-map</b> to unlink. Existing mappings are listed below.
        </div> */}
        <div className="map-info-note">
          <strong>Mapping:</strong> SAU device must be in <em>Demo/Sold/Rental</em> state.
          Gandhvedh must be in <em>Mapped to Owner</em> state.
          After mapping, the SAU device moves to <em>Mapped to Owner/Mapped as Replacement</em> state.
          <br />
          <strong>De-mapping:</strong> Use the <em>De-map</em> button directly in the table row below.
          No re-verification needed. After de-mapping, the SAU device moves to <em>Ready for shipment</em> state.
        </div>

        {/* ── Status banner ── */}
        {mappingStatus && (
          <div className={`map-status-banner ${mappingStatus.success ? "map-status-success" : "map-status-fail"}`}>
            {mappingStatus.success ? <CheckCircleFilled /> : <CloseCircleFilled />}
            <span>{mappingStatus.message}</span>
            <button className="map-dismiss-btn" onClick={() => setMappingStatus(null)}>✕</button>
          </div>
        )}

        {/* ── Device cards ── */}
        <div className="map-cards-row">
          <DeviceCard
            label="Air Freshner Device"
            icon={MdOutlineAir}
            accentClass="card-af"
            device={airFreshner}
            setter={setAirFreshner}
            deviceKind={DEVICE_TYPE_SAU}
            placeholder="Enter Device ID"
            inputRef={afInputRef}
            onChangeID={onChangeID}
            toggleScan={toggleScan}
            verifyDevice={verifyDevice}
            onScan={onScan}
          />

          <div className="map-connector">
            <div className={`map-connector-line ${bothVerified ? "active" : ""}`} />
            <div className={`map-connector-dot ${bothVerified ? "active" : ""}`}><FaLink /></div>
            <div className={`map-connector-line ${bothVerified ? "active" : ""}`} />
          </div>

          <DeviceCard
            label="Gandhvedh Device"
            icon={TbDeviceAnalytics}
            accentClass="card-gv"
            device={gandhvedh}
            setter={setGandhvedh}
            deviceKind={DEVICE_TYPE_HHM}
            placeholder="Enter Device ID"
            inputRef={gvInputRef}
            onChangeID={onChangeID}
            toggleScan={toggleScan}
            verifyDevice={verifyDevice}
            onScan={onScan}
          />
        </div>

        {/* ── Action buttons ── */}
        <div className="map-action-row">
          <button
            className={`map-action-btn btn-map ${bothVerified && !actionLoading ? "" : "disabled"}`}
            onClick={confirmMapping}
            disabled={!bothVerified || actionLoading}
          >
            <FaLink />
            {actionLoading ? "Processing…" : "Map Devices"}
          </button>
        </div>

        {!bothVerified && (
          <p className="map-helper-text">Verify both devices above to enable Map Devices Button.</p>
        )}

        {/* ════════════════════════════════════════
            MAPPED DEVICES REPORT
        ════════════════════════════════════════ */}
        {/* <div className="map-report-section">

          <div className="map-report-header">
            <div className="map-report-title-row">
              <h3 className="map-report-title">Existing Mappings</h3>
              {!reportLoading && (
                <span className="map-report-count">{filteredList.length} record{filteredList.length !== 1 ? "s" : ""}</span>
              )}
            </div>
            <div className="map-report-controls">
              <div className="map-search-wrap">
                <input
                  type="text"
                  className="map-search-input"
                  placeholder="Search by Device ID…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="map-search-clear" onClick={() => setSearchTerm("")}><FaTimes /></button>
                )}
              </div>
              <button
                className="map-refresh-btn"
                onClick={fetchMappedDevices}
                disabled={reportLoading}
                title="Refresh"
              >
                <FaSyncAlt className={reportLoading ? "spin" : ""} />
              </button>
            </div>
          </div>

          {reportError && (
            <div className="map-report-error"><CloseCircleFilled /> {reportError}</div>
          )}

          {reportLoading ? (
            <div className="map-report-loading">
              <div className="map-spinner" />
              <span>Loading mappings…</span>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="map-report-empty">
              {searchTerm ? "No mappings match your search." : "No mapped devices found."}
            </div>
          ) : (
            <div className="map-table-wrap">
              <table className="map-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th><span className="map-th-af">SAU Device ID</span></th>
                    <th><span className="map-th-gv">HHM Device ID</span></th>
                    <th>Status</th>
                    <th>Mapped On</th>
                    <th>Mapped Requested By</th>
                    <th>De-mapped On</th>
                    <th>De-mapped Requested By</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((row, index) => {
                    const key = `${row.SAUDeviceID}_${row.HHMDeviceID}`;
                    const rowStatus = demapRowStatus[key] || {};
                    const isActive = row.Status === "Mapped" || row.Status === "MapReceived" || row.Status === "MapRequested";
                    return (
                      <tr key={`${key}_${index}`}>
                        <td className="map-td-num">{index + 1}</td>
                        <td><span className="map-device-chip">{row.SAUDeviceID}</span></td>
                        <td><span className="map-device-chip">{row.HHMDeviceID}</span></td>
                        <td>
                          <span >
                            {row.Status}
                          </span>
                        </td>
                        <td className="map-td-date">{formatDate(row.MappedOnDtTm)}</td>
                        <td className="map-td-user">{row.MapRequestedByUser || "—"}</td>
                        <td className="map-td-date">{formatDate(row.DemappedOnDtTm)}</td>
                        <td className="map-td-user">{row.DemapRequestedByUser || "-"}</td>
                        <td>
                          {isActive ? (
                            <div className="map-row-action-wrap">
                              <button
                                className="map-row-demap-btn"
                                onClick={() => confirmDemapping(row)}
                                disabled={rowStatus.loading}
                                title="De-map this pair"
                              >
                                <FaUnlink />
                                {rowStatus.loading ? "Removing…" : "De-map"}
                              </button>
                              {rowStatus.error && <span className="map-row-error">{rowStatus.error}</span>}
                            </div>
                          ) : (
                            <span className="map-td-na">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
          }
        </div> */}
        <div className="map-report-section">

          <div className="map-report-header">
            <div className="map-report-title-row">
              <h3 className="map-report-title">Existing Mappings</h3>
              {!reportLoading && (
                <span className="map-report-count">
                  {mappedList.length} record{mappedList.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="map-report-controls">
              {/* External search removed — each column now has its own filter box */}
              <button
                className="map-refresh-btn"
                onClick={fetchMappedDevices}
                disabled={reportLoading}
                title="Refresh"
              >
                <FaSyncAlt className={reportLoading ? "spin" : ""} />
              </button>
            </div>
          </div>

          {reportError && (
            <div className="map-report-error"><CloseCircleFilled /> {reportError}</div>
          )}

          {reportLoading ? (
            <div className="map-report-loading">
              <div className="map-spinner" />
              <span>Loading mappings…</span>
            </div>
          ) : (
            <MappingReactTable
              columns={mappingTableColumns}
              data={mappedList}
              passedStateVariable={!reportLoading}
            />
          )}

        </div>
      </div>
      <Modal
        title="Spray Count"
        open={sprayCountRow !== null}
        onCancel={() => { if (!sprayCountLoading) setSprayCountRow(null); }}
        closable={!sprayCountLoading}
        maskClosable={!sprayCountLoading}
        keyboard={!sprayCountLoading}
        width={380}
        centered
        footer={[
          <Button
            key="update"
            onClick={handleSprayCountUpdate}
            loading={sprayCountLoading}
            type="primary"
            className="map-spray-modal-btn"
            disabled={!Number.isSafeInteger(sprayCount) || sprayCount < 0}
          >
            Update
          </Button>,
        ]}
      >
        <label htmlFor="map-spray-count" className="map-spray-count-label">
          Number of Spray Counts
        </label>
        <InputNumber
          id="map-spray-count"
          min={0}
          max={Number.MAX_SAFE_INTEGER}
          step={1}
          value={sprayCount}
          disabled={sprayCountLoading}
          onChange={setSprayCount}
          placeholder="Enter spray count"
          style={{ width: "100%" }}
        />
        {sprayCountError && <div className="map-field-error" role="alert">{sprayCountError}</div>}
      </Modal>
    </div>
  );
}

export default VcMapAirFreshner;
